/**
 * The guided repair, headless — what the Body tab's "World check" section shows
 * for an intake session (ADR-040 §3). Each finding of the armature check
 * becomes ONE card with a status mark, mapped from ADR-224 §4.4's
 * finding → fix → question table:
 *
 *   done for you   a fix with no decision (rename, re-seat): `fine`, pill
 *                  "done for you"; it happens at the export, and the numbers
 *                  go to the status line, never the card;
 *   a question     asked at the object (fingers, a tail): `suggested`, with a
 *                  button that opens the section where the object's tool
 *                  already lives — the hands, Tails & cloth. Skipping is a
 *                  valid answer; nothing here acts on an unconfirmed offer;
 *   a stop         nothing Harry can do today: `needs`, said plainly.
 *
 * Then the two cards every pass ends on: the walk preview and the export. The
 * person reads decisions here; the readings ride the status line (Allen
 * 2026-09-29, "panels carry decisions, not details").
 *
 * World's own words (`verdict.reasons`, `name`) are DISPLAY ONLY: they are
 * carried to the panel as text, never parsed (the panel sets textContent).
 *
 * PURE: no DOM. Depends on: kernel (report types), status-mark (state type).
 * Depended on by: shell/intake-panel.ts, main.ts (progress), tests.
 */
import type { RigCheckCode, RigCheckFinding, RigCheckReport, RigCheckVerdict } from '@harryhausen/kernel';
import type { StatusState } from '../shell/status-mark.js';

/** Where a question's tool lives: a Body section the panel opens. */
export type IntakeJump = 'joints' | 'parts';

export interface IntakeCard {
  readonly code: RigCheckCode | 'walk' | 'export' | 'world';
  readonly state: StatusState;
  /** The pill beside the mark; null = none. */
  readonly pill: string | null;
  readonly title: string;
  readonly line: string;
  readonly jump?: { readonly to: IntakeJump; readonly label: string };
}

/** What the session knows; the view reads nothing else. */
export interface IntakeViewState {
  readonly origin: { readonly kind: 'world'; readonly name: string; readonly reasons: readonly string[] } | { readonly kind: 'opt-in' };
  readonly checking: boolean;
  /** A check that could not run, in the import door's words. */
  readonly error: string | null;
  readonly report: RigCheckReport | null;
  /** The subject on stage is the checked file (false while the file could not load — a static mesh). */
  readonly onStage: boolean;
  readonly exported: { readonly savedForWorld: boolean; readonly line: string } | null;
  /** The guided flow's position and answers (INTAKE_GUIDED_FLOW §3.7); absent = not moved yet (`FLOW_START`). */
  readonly flow?: IntakeFlow;
}

/** Where the guided flow stands. */
export type IntakeStep = 'wrong' | 'ask-fingers' | 'doing-fingers' | 'ask-tail' | 'doing-tail' | 'fix' | 'check';
export interface IntakeAnswers {
  readonly fingers: 'keep' | 'add' | null;
  readonly tail: 'still' | 'sway' | null;
}
/** The build the Fix step makes on the click (no save dialog), and its re-check. */
export type IntakeBuild =
  | { readonly status: 'none' }
  | { readonly status: 'building' }
  | { readonly status: 'built'; readonly receipts: readonly string[] }
  /** F2: the built bytes still fail World's check. */
  | { readonly status: 'refused'; readonly reasons: readonly string[] }
  /** F4: another file reached the stage while Harry was building. */
  | { readonly status: 'stage-changed' }
  | { readonly status: 'failed'; readonly reason: string };
export type IntakeWalk = { readonly status: 'idle' } | { readonly status: 'loading' } | { readonly status: 'playing' } | { readonly status: 'unavailable'; readonly reason: string };
/** F: Send back to World, from the bytes Check showed. */
export type IntakeSend =
  | { readonly status: 'idle' }
  | { readonly status: 'sending' }
  | { readonly status: 'sent' }
  /** F1: storage refused the record (private window, quota). */
  | { readonly status: 'blocked'; readonly reason: string }
  /** F3: no World person to hand it to (an opt-in, nobody signed in to World here). */
  | { readonly status: 'no-user' }
  /** F4: another file reached the stage since the build. */
  | { readonly status: 'stage-changed' };
export interface IntakeFlow {
  readonly step: IntakeStep;
  readonly answers: IntakeAnswers;
  /**
   * Which questions this body asks (fingers: no digits; tail: a tail Harry can let sway — rigged, or
   * `tailUnrigged`: a tail the mesh has and the skeleton lacks, which Harry gives bones first).
   */
  readonly questions: { readonly fingers: boolean; readonly tail: boolean; readonly tailUnrigged?: boolean };
  readonly build: IntakeBuild;
  readonly walk: IntakeWalk;
  /** Absent = idle (a flow from before PR 3's send). */
  readonly send?: IntakeSend;
  /** A copy was saved (Save a copy, F1, F3). */
  readonly saved?: boolean;
}

const VERDICT_WORDS: Readonly<Record<RigCheckVerdict, string>> = {
  works: 'Ready for World.',
  limited: 'Ready for World, with limits.',
  'needs-harry': 'Harry can fix this for World.',
  unsupported: "Harry can't fix this for World yet.",
};

/**
 * One finding → one card. EVERY code has a row (the closed list is pinned by
 * intake-view.test.ts), so a new code cannot arrive in the panel unworded.
 */
export function cardForFinding(f: RigCheckFinding): IntakeCard {
  switch (f.code) {
    case 'family-match':
      return { code: f.code, state: 'fine', pill: null, title: 'Skeleton', line: "Matches World's animations." };
    case 'family-near-miss':
      return { code: f.code, state: 'fine', pill: 'done for you', title: 'Bone names', line: "Renamed to World's skeleton when you export." };
    case 'family-none':
      return f.severity === 'unsupported'
        ? { code: f.code, state: 'needs', pill: "can't fix yet", title: 'Skeleton', line: f.message }
        : { code: f.code, state: 'fine', pill: 'done for you', title: 'Bone names', line: "Mapped onto World's skeleton when you export." };
    case 'bind-frames':
      return { code: f.code, state: 'fine', pill: 'done for you', title: 'Arm and leg frames', line: 'Re-seated when you export, so animations play without twisting.' };
    case 'frames-unmeasured':
      return { code: f.code, state: 'check', pill: 'unmeasured', title: 'Arm and leg frames', line: "Harry couldn't measure them. The export checks again." };
    case 'no-fingers':
      return {
        code: f.code,
        state: 'suggested',
        pill: null,
        title: 'Fingers',
        line: 'No fingers, so hand poses won\'t show. Add them, or skip: World plays the body without them.',
        jump: { to: 'joints', label: 'Add fingers ›' },
      };
    case 'tail-rigid':
      return {
        code: f.code,
        state: 'suggested',
        pill: null,
        title: 'Tail',
        line: "The tail won't move on its own. Give it a spring, or leave it still.",
        jump: { to: 'parts', label: 'Tail settings ›' },
      };
    case 'tail-springs':
      return { code: f.code, state: 'fine', pill: null, title: 'Tail', line: 'Sways on its own.' };
    case 'tail-unrigged':
      return {
        code: f.code,
        state: 'suggested',
        pill: null,
        title: 'Tail',
        line: "The tail has no bones yet, so it won't move. Give it bones, or leave it.",
        jump: { to: 'parts', label: 'Tail settings ›' },
      };
    case 'no-skeleton':
      return { code: f.code, state: 'needs', pill: 'coming soon', title: 'Skeleton', line: 'This model has no skeleton, so it can\'t animate. Harry can add a skeleton — coming soon.' };
    case 'vrm-declared':
      return { code: f.code, state: 'needs', pill: "can't fix", title: 'VRM file', line: f.message };
  }
}

/** The fixes the export will make, from the findings (the done-for-you list). */
export function plannedFixes(report: RigCheckReport): string[] {
  return report.findings.flatMap((f) => {
    if (f.code === 'family-near-miss' || (f.code === 'family-none' && f.severity === 'needs-harry')) return ["rename to World's skeleton"];
    if (f.code === 'bind-frames') return ['re-seat arm and leg frames'];
    return [];
  });
}

export interface IntakeView {
  /** The headline: what Harry's own check says about the file. */
  readonly headline: string;
  /** World's reasons, as World wrote them (display only); empty for an opt-in. */
  readonly worldSaid: readonly string[];
  readonly cards: readonly IntakeCard[];
  /** The export may run: the file is on stage and nothing is a stop. */
  readonly canExport: boolean;
}

export function intakeView(s: IntakeViewState): IntakeView {
  const worldSaid = s.origin.kind === 'world' ? s.origin.reasons : [];
  if (s.checking) return { headline: 'Checking the skeleton…', worldSaid, cards: [], canExport: false };
  if (s.error !== null) {
    return {
      headline: "Harry couldn't read this file.",
      worldSaid,
      cards: [{ code: 'world', state: 'needs', pill: null, title: 'File', line: s.error }],
      canExport: false,
    };
  }
  if (s.report === null) return { headline: 'No check yet.', worldSaid, cards: [], canExport: false };
  const findings = s.report.findings.map(cardForFinding);
  const stopped = s.report.verdict === 'unsupported' || !s.onStage;
  const tail: IntakeCard[] = stopped
    ? []
    : [
        {
          code: 'walk',
          state: 'suggested',
          pill: null,
          title: 'Walk preview',
          line: 'Watch your character walk before you export.',
        },
        s.exported === null
          ? { code: 'export', state: 'needs', pill: null, title: 'Export for World', line: 'Saves the fixed file, and leaves it for World to offer back to you.' }
          : { code: 'export', state: s.exported.savedForWorld ? 'yours' : 'check', pill: s.exported.savedForWorld ? 'saved' : 'not sent', title: 'Export for World', line: s.exported.line },
      ];
  return { headline: VERDICT_WORDS[s.report.verdict], worldSaid, cards: [...findings, ...tail], canExport: !stopped };
}

// ─────────────────────────── the guided flow (INTAKE_GUIDED_FLOW_2026-10-03) ───────────────────────────
//
// `intakeSteps` is the SECOND pure resolver over the same session state `intakeView` reads: the
// newcomer's one-step-per-screen frame. The ledger (`intakeView`) stays as the expert's view; both read
// one session, so they cannot drift. PR 2 of 3: What's wrong (B), the questions inside Fix (C1 fingers,
// C1b adding them, C2 the tail), Fix (D, with F2 and F4), and Check (E) on World's walk; Send is still
// today's export door until PR 3 (design §5).

/** The four rail labels, always printed (§3.1: the rail never grows). */
export const RAIL_STEPS = ["What's wrong", 'Fix', 'Check', 'Send'] as const;
export type RailStep = (typeof RAIL_STEPS)[number];

/** One rail stop: `yours` = done with a click, `needs` = the current step, null = not yet (not drawn). */
export interface RailEntry {
  readonly label: RailStep;
  readonly mark: 'yours' | 'needs' | null;
}

/** What a button does; the frame maps each to a session call. Nothing here acts. */
export type StepAction =
  | 'open'
  | 'not-now'
  | 'next'
  | 'keep-hands'
  | 'add-fingers'
  | 'fingers-done'
  | 'let-sway'
  | 'keep-still'
  | 'grow-tail'
  | 'tail-done'
  | 'tail-leave'
  | 'keep-improving'
  | 'fix-again'
  | 'see-walk'
  | 'retry-walk'
  | 'send'
  | 'save-copy'
  | 'back'
  | 'full-editor'
  | 'back-to-world'
  | 'keep-fixing'
  | 'stop';

export type ScreenId =
  | 'arriving'
  | 'opening'
  | 'nothing-waiting'
  | 'wrong'
  | 'ask-fingers'
  | 'doing-fingers'
  | 'ask-tail'
  | 'doing-tail'
  | 'fixing'
  | 'fixed'
  | 'fix-refused'
  | 'stage-changed'
  | 'check'
  | 'check-no-walk'
  | 'sending'
  | 'sent'
  | 'storage-blocked'
  | 'save-for-world'
  | 'send-stage-changed'
  | 'not-sent'
  | 'stop-no-skeleton'
  | 'stop-vrm'
  | 'stop-refused'
  | 'stop-unreadable'
  | 'confirm-leave';

/**
 * Where the stage looks: `absent` = no stage (before Open, a file that never reached it); `headshot` =
 * their face, at rest; `hands` = at the hands (the fingers question, and adding them — the hand tools'
 * markers are drawn only there); `body` = the whole body (the tail question); `tail` = at the tail with
 * its editor (giving it bones: the bones drawn, the bar open, the wag playing, the camera free); `proof` =
 * the isolated proof stage playing World's walk on the built file.
 */
export type StageView = 'absent' | 'headshot' | 'hands' | 'body' | 'tail' | 'proof';

export interface StepScreen {
  readonly id: ScreenId;
  /** Null = no rail (arriving, the stops: there is no path to count). */
  readonly rail: readonly RailEntry[] | null;
  readonly title: string;
  readonly body: readonly string[];
  /** Short marked lines (the done-for-you list), each a `fine` mark. */
  readonly lines: readonly string[];
  /** A closed disclosure: World's words, a stop's reasons, the receipts — verbatim, display only. */
  readonly disclosure: { readonly label: string; readonly lines: readonly string[] } | null;
  readonly primary: { readonly label: string; readonly action: StepAction } | null;
  readonly secondary: { readonly label: string; readonly action: StepAction } | null;
  /** A further quiet door beside the secondary (the Sent card's Save a copy). */
  readonly also: { readonly label: string; readonly action: StepAction } | null;
  readonly stage: StageView;
  /** An indeterminate wait (opening, checking, fixing): no buttons. */
  readonly busy: boolean;
  /** A slim instruction bar under the header instead of the card (adding fingers works at the hand). */
  readonly slim: boolean;
  /** One instruction in large type above the buttons (the browser kept this tab open: where World is). */
  readonly notice: string | null;
}

/** The guided frame's own state, beside the session's. */
export interface GuidedState {
  /** Before a session: World's offer waiting for Open, or nothing to open. */
  readonly arrival: { readonly kind: 'offer'; readonly name: string; readonly ageMs: number } | { readonly kind: 'missing' } | null;
  readonly session: IntakeViewState | null;
  readonly confirmLeave: boolean;
  /** The avatar's display name source: World's `name`, or the stage's label for an opt-in. Display only. */
  readonly name: string;
  /** The person's browser refused to close the tab on Back to World: say where World is (design §3.6). */
  readonly stayed?: boolean;
  /** No World tab behind this one, but a World tab answered on the channel that it has the file (world-2d). */
  readonly worldHasIt?: boolean;
  /** Is the World tab that opened Harry still there (`window.opener`, alive)? Absent = assume it is. */
  readonly worldTab?: 'open' | 'gone';
}

/**
 * A name a person would give it (Allen, 2026-10-03): the generator's junk dropped, Title Case. Display
 * only — the bytes and every record keep the file's own name.
 *   black_lace_dress_3d_model_glb_mixamo_com_2026-10-01 → Black Lace Dress
 *   anthropomorphic+red+fox+3d+model                     → Anthropomorphic Red Fox
 *   tripo-kobold-actorcore                               → Kobold
 */
export function avatarName(raw: string): string {
  let s = raw.trim().replace(/\.(glb|vrm|gltf|fbx)$/i, '');
  s = s.replace(/[\s_+.-]*glb[\s_+.-]*mixamo[\s_+.-]*com.*$/i, ''); // a Mixamo round trip's tail
  s = s.replace(/[\s_+.-]*3d[\s_+.-]*model/gi, ''); // "3d model" / "+3d+model" / "_3d_model"
  s = s.replace(/[\s_+.-]*\d{4}-\d{2}-\d{2}(?:[T_ ]\d{2}[-:]?\d{2}(?:[-:]?\d{2})?)?/g, ''); // dates
  s = s.replace(/[\s_+.-]*\(\d+\)$/, ''); // a browser's " (1)"
  s = s.replace(/[\s_+.-]*v\d+$/i, ''); // "_v4"
  s = s.replace(/^tripo[\s_+.-]+/i, ''); // the generator prefix
  for (;;) {
    const next = s.replace(/[\s_+.-]+(actorcore|mixamo|ue5|unity|vrm1|vrm|raw|tripo)$/i, ''); // preset suffixes
    if (next === s) break;
    s = next;
  }
  const words = s
    .split(/[\s_+.-]+/)
    .filter((w) => w !== '')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1));
  return words.length === 0 ? 'your avatar' : words.join(' ');
}

const RAIL_NONE: readonly RailEntry[] = RAIL_STEPS.map((label) => ({ label, mark: null }));
function rail(current: RailStep | null, done: readonly RailStep[]): readonly RailEntry[] {
  return RAIL_NONE.map((r) => ({ label: r.label, mark: done.includes(r.label) ? 'yours' : r.label === current ? 'needs' : null }));
}

type ScreenParts = Omit<StepScreen, 'lines' | 'disclosure' | 'busy' | 'slim' | 'notice' | 'also'> & Partial<Pick<StepScreen, 'lines' | 'disclosure' | 'busy' | 'slim' | 'notice' | 'also'>>;
const screen = (s: ScreenParts): StepScreen => ({ lines: [], disclosure: null, busy: false, slim: false, notice: null, also: null, ...s });

/** The done-for-you lines in the newcomer's words, before the fix (§3.2: no bone or frame jargon on a primary path). */
export function fixLines(report: RigCheckReport): string[] {
  const out: string[] = [];
  for (const f of report.findings) {
    if (f.code === 'family-near-miss' || (f.code === 'family-none' && f.severity === 'needs-harry')) out.push('Bone names will match World');
    else if (f.code === 'bind-frames') out.push('Arms and legs will no longer twist when they move');
    else if (f.code === 'frames-unmeasured') out.push("Harry couldn't measure the arms and legs, so it checks them again when it saves");
  }
  return [...new Set(out)];
}

/** The same lines, after the fix: what Harry did. */
export function fixedLines(report: RigCheckReport, answers: IntakeAnswers, name: string, tailGrown = false): string[] {
  const out: string[] = [];
  for (const f of report.findings) {
    if (f.code === 'family-near-miss' || (f.code === 'family-none' && f.severity === 'needs-harry')) out.push('Bone names now match World');
    else if (f.code === 'bind-frames') out.push('Arms and legs no longer twist when they move');
  }
  if (answers.tail === 'sway') out.push(tailGrown ? 'Tail now moves' : `The tail sways as ${name} moves`);
  if (out.length === 0) out.push('Harry checked the file again, and World can use it');
  return [...new Set(out)];
}

/** The stop a report ends on, or null when the flow can go on. */
export function stopOf(report: RigCheckReport): RigCheckFinding | null {
  return (
    report.findings.find((f) => f.code === 'no-skeleton') ??
    report.findings.find((f) => f.code === 'vrm-declared') ??
    report.findings.find((f) => f.severity === 'unsupported') ??
    null
  );
}

const OPENING_BODY = ['Big avatars can take a minute.'];
const RAIL_WRONG = rail("What's wrong", []);
const RAIL_FIX = rail('Fix', ["What's wrong"]);
const RAIL_CHECK = rail('Check', ["What's wrong", 'Fix']);
const RAIL_SEND = rail('Send', ["What's wrong", 'Fix', 'Check']);
const RAIL_DONE = rail(null, ["What's wrong", 'Fix', 'Check', 'Send']);

/** The flow's default, for a session that has not moved yet. */
export const FLOW_START: IntakeFlow = {
  step: 'wrong',
  answers: { fingers: null, tail: null },
  questions: { fingers: false, tail: false },
  build: { status: 'none' },
  walk: { status: 'idle' },
};

/**
 * The one screen the guided frame shows. Pure: the same input always draws the same screen, and the
 * screen only NAMES actions — the frame performs them on a click (questions never act).
 */
export function intakeSteps(g: GuidedState): StepScreen {
  const out = resolveStep(g);
  // A World tab has the file (world-2d ack, no opener): say so, big — no new tab is opened.
  if (g.worldHasIt === true && (out.primary?.action === 'back-to-world' || out.secondary?.action === 'back-to-world')) {
    return { ...out, notice: 'Your World tab has it. Switch to it.' };
  }
  // Where World is, when the browser kept this tab open: on every screen whose way out is Back to World.
  if (g.stayed === true && out.notice === null && (out.primary?.action === 'back-to-world' || out.secondary?.action === 'back-to-world')) {
    return { ...out, notice: 'Your browser kept you here. Click your World tab at the top of the window.' };
  }
  return out;
}

function resolveStep(g: GuidedState): StepScreen {
  const name = avatarName(g.name);
  const s = g.session;
  const flow = s?.flow ?? FLOW_START;
  if (g.confirmLeave) {
    const flowing = s !== null && s.report !== null && stopOf(s.report) === null;
    return screen({
      id: 'confirm-leave',
      rail: flowing ? railOf(flow) : null,
      title: `Stop fixing ${name}?`,
      body: ['World still has your original. You can start again with Open in Harry.'],
      primary: { label: 'Keep fixing', action: 'keep-fixing' },
      secondary: { label: 'Stop', action: 'stop' },
      stage: s?.onStage === true ? 'headshot' : 'absent',
    });
  }
  if (s === null) {
    if (g.arrival?.kind === 'offer') {
      const minutes = Math.round(g.arrival.ageMs / 60_000);
      const when = minutes < 1 ? 'just now' : minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
      return screen({
        id: 'arriving',
        rail: null,
        title: 'Fix your avatar for World',
        body: [`World sent ${avatarName(g.arrival.name)}, ${when}.`, 'Harry will check it and fix what World needs. Nothing changes until you say so.'],
        primary: { label: 'Open it ›', action: 'open' },
        secondary: { label: 'Not now', action: 'not-now' },
        stage: 'absent',
      });
    }
    return screen({
      id: 'nothing-waiting',
      rail: null,
      title: "Harry couldn't find your avatar from World",
      body: ['Drag the file onto this window, or go back to World and choose Open in Harry again.'],
      primary: null,
      secondary: { label: 'Full editor', action: 'full-editor' },
      stage: 'absent',
    });
  }
  const stayed: string[] = [];
  const notice = g.stayed === true ? 'Your browser kept you here. Click your World tab at the top of the window.' : null;
  if (s.error !== null) {
    return screen({
      id: 'stop-unreadable',
      rail: null,
      title: "Harry couldn't open this file",
      body: ['It may be damaged, or not a 3D model file.', ...stayed],
      disclosure: { label: 'Why', lines: [s.error] },
      primary: { label: 'Back to World ›', action: 'back-to-world' },
      secondary: { label: 'Full editor', action: 'full-editor' },
      stage: 'absent',
      notice,
    });
  }
  if (s.checking || s.report === null) {
    return screen({ id: 'opening', rail: null, title: s.onStage ? `Checking ${name}…` : `Opening ${name}…`, body: OPENING_BODY, primary: null, secondary: null, stage: s.onStage ? 'headshot' : 'absent', busy: true });
  }
  const report = s.report;
  const stop = stopOf(report);
  if (stop !== null) {
    const stage = s.onStage && stop.code !== 'no-skeleton' ? 'headshot' : 'absent';
    if (stop.code === 'no-skeleton') {
      return screen({
        id: 'stop-no-skeleton',
        rail: null,
        title: `${name} has no skeleton yet`,
        body: [
          'A skeleton is what lets an avatar move. Harry will be able to add one soon.',
          'For now, use the Rig or Animate step in the app that made your model, then upload it to World again.',
          ...stayed,
        ],
        primary: { label: 'Back to World ›', action: 'back-to-world' },
        secondary: { label: 'Full editor', action: 'full-editor' },
        stage: 'absent',
      });
    }
    if (stop.code === 'vrm-declared') {
      return screen({
        id: 'stop-vrm',
        rail: null,
        title: "Harry can't change a VRM",
        body: ['Changing it here would lose its license and its expressions.', 'Fix it in the app that made it, such as VRoid Studio, then upload it again.', ...stayed],
        primary: { label: 'Back to World ›', action: 'back-to-world' },
        secondary: { label: 'Full editor', action: 'full-editor' },
        stage,
      });
    }
    return screen({
      id: 'stop-refused',
      rail: null,
      title: "Harry can't fix this one yet",
      body: ["Its skeleton is too different from World's for Harry to change safely.", ...stayed],
      disclosure: { label: 'Why', lines: [stop.message] },
      primary: { label: 'Back to World ›', action: 'back-to-world' },
      secondary: { label: 'Full editor', action: 'full-editor' },
      stage,
    });
  }
  if (!s.onStage) {
    // Checked, not yet on stage (the load door is walking): still opening.
    return screen({ id: 'opening', rail: null, title: `Opening ${name}…`, body: OPENING_BODY, primary: null, secondary: null, stage: 'absent', busy: true });
  }
  const worldSaid = s.origin.kind === 'world' && s.origin.reasons.length > 0 ? { label: 'What World said', lines: s.origin.reasons } : null;
  if (s.exported !== null) {
    return s.exported.savedForWorld
      ? screen({
          id: 'sent',
          rail: RAIL_DONE,
          title: 'Saved and sent to World',
          body: [`Switch back to your World tab. It will offer you the fixed ${name}.`, ...stayed],
          primary: { label: 'Back to World ›', action: 'back-to-world' },
          secondary: { label: 'Keep improving in Harry ›', action: 'keep-improving' },
          stage: flow.build.status === 'built' ? 'proof' : 'headshot',
        })
      : screen({
          id: 'not-sent',
          rail: RAIL_SEND,
          title: 'Not sent to World',
          body: [s.exported.line, ...stayed],
          primary: { label: 'Back to World ›', action: 'back-to-world' },
          secondary: { label: 'Full editor', action: 'full-editor' },
          stage: 'headshot',
        });
  }
  const limits = report.findings.filter((f) => f.severity === 'limit').length;
  const questionsPending = (flow.questions.fingers && flow.answers.fingers === null) || (flow.questions.tail && flow.answers.tail === null);
  switch (flow.step) {
    case 'ask-fingers':
      return screen({
        id: 'ask-fingers',
        rail: RAIL_FIX,
        title: `${name} has no fingers`,
        body: ['World will play it with closed hands. You can add fingers now. It takes a few minutes.'],
        primary: { label: 'Keep hands ›', action: 'keep-hands' },
        secondary: { label: 'Add fingers', action: 'add-fingers' },
        stage: 'hands',
      });
    case 'doing-fingers':
      return screen({
        id: 'doing-fingers',
        rail: RAIL_FIX,
        title: 'Add fingers',
        body: ['Click the tip of each finger on the model, then name it.'],
        primary: { label: 'Done ›', action: 'fingers-done' },
        secondary: null,
        stage: 'hands',
        slim: true,
      });
    case 'ask-tail':
      // An UNRIGGED tail (the mesh has one, the skeleton doesn't): shown framed before the person answers —
      // an offer may be wrong, and "Leave it" writes nothing.
      return flow.questions.tailUnrigged === true
        ? screen({
            id: 'ask-tail',
            rail: RAIL_FIX,
            title: `${name} has a tail that doesn't move yet`,
            body: ['Harry can give it bones and let it sway. You will see it wag and can change it before anything is kept. This is optional.'],
            primary: { label: 'Give it bones ›', action: 'grow-tail' },
            secondary: { label: 'Leave it', action: 'keep-still' },
            stage: 'body',
          })
        : screen({
            id: 'ask-tail',
            rail: RAIL_FIX,
            title: `${name} has a tail`,
            body: [`Right now it stays stiff. Harry can let it sway as ${name} moves.`],
            primary: { label: 'Let it sway ›', action: 'let-sway' },
            secondary: { label: 'Keep it still', action: 'keep-still' },
            stage: 'body',
          });
    case 'doing-tail':
      // The tail's own editor, focused (C2b, Allen's rule: show it, let them fix it): the bones drawn, the
      // tail's bar open, the wag playing. Done keeps it; Leave it takes the bones back off.
      return screen({
        id: 'doing-tail',
        rail: RAIL_FIX,
        title: 'Give it bones',
        body: ["Watch it wag. Use the tail's bar for more or fewer bones, and Tails & cloth for the wag. Done when it looks right."],
        primary: { label: 'Done ›', action: 'tail-done' },
        secondary: { label: 'Leave it', action: 'tail-leave' },
        stage: 'tail',
        slim: true,
      });
    case 'fix':
      return fixScreen(flow, report, name, questionsPending);
    case 'check':
      return flow.send !== undefined && flow.send.status !== 'idle' ? sendScreen(flow.send, flow.saved === true, name, g.worldHasIt === true ? 'open' : (g.worldTab ?? 'open'), notice, flow.walk.status === 'playing' ? 'proof' : 'headshot') : checkScreen(flow, name);
    case 'wrong':
    default:
      break;
  }
  if (report.verdict === 'works') {
    return screen({
      id: 'wrong',
      rail: RAIL_WRONG,
      title: `${name} is ready for World`,
      body: [`Nothing needs fixing. ${name} should already work in World.`],
      disclosure: worldSaid,
      primary: { label: 'Send back to World ›', action: 'next' },
      secondary: null,
      stage: 'headshot',
    });
  }
  if (report.verdict === 'limited') {
    return screen({
      id: 'wrong',
      rail: RAIL_WRONG,
      title: `${name} works in World`,
      body: [`${name} already works in World. There ${limits === 1 ? 'is one thing' : `are ${limits} things`} you could improve first.`],
      disclosure: worldSaid,
      primary: { label: 'Next ›', action: 'next' },
      secondary: null,
      stage: 'headshot',
    });
  }
  return screen({
    id: 'wrong',
    rail: RAIL_WRONG,
    title: `World can't animate ${name} yet`,
    body: ["Its skeleton is set up a little differently from World's. Harry can fix that for you."],
    disclosure: worldSaid,
    primary: { label: 'Fix it ›', action: 'next' },
    secondary: null,
    stage: 'headshot',
  });
}

function railOf(flow: IntakeFlow): readonly RailEntry[] {
  if (flow.step === 'wrong') return RAIL_WRONG;
  if (flow.step === 'check') return RAIL_CHECK;
  return RAIL_FIX;
}

function fixScreen(flow: IntakeFlow, report: RigCheckReport, name: string, questionsPending = false): StepScreen {
  const b = flow.build;
  if (b.status === 'refused') {
    return screen({
      id: 'fix-refused',
      rail: RAIL_FIX,
      title: "Harry couldn't fix everything",
      body: [`Harry couldn't fix everything World needs: ${b.reasons.join(' ')}`, 'Nothing was sent to World.'],
      primary: { label: 'Back to World ›', action: 'back-to-world' },
      secondary: { label: 'Full editor', action: 'full-editor' },
      stage: 'headshot',
    });
  }
  if (b.status === 'stage-changed') {
    return screen({
      id: 'stage-changed',
      rail: RAIL_FIX,
      title: 'Another file was opened',
      body: [`Another file was opened while Harry was fixing ${name}. Nothing was sent.`],
      primary: { label: 'Fix it again ›', action: 'fix-again' },
      secondary: { label: '‹ Back', action: 'back' },
      stage: 'headshot',
    });
  }
  if (b.status === 'failed') {
    return screen({
      id: 'fix-refused',
      rail: RAIL_FIX,
      title: "Harry couldn't fix it",
      body: ['Something went wrong while Harry was building the fixed file. Nothing was sent.'],
      disclosure: { label: 'Why', lines: [b.reason] },
      primary: { label: 'Fix it again ›', action: 'fix-again' },
      secondary: { label: 'Full editor', action: 'full-editor' },
      stage: 'headshot',
    });
  }
  if (b.status === 'built') {
    return screen({
      id: 'fixed',
      rail: RAIL_FIX,
      title: 'Fixed',
      body: [],
      lines: fixedLines(report, flow.answers, name, flow.questions.tailUnrigged === true),
      disclosure: b.receipts.length > 0 ? { label: 'What changed', lines: b.receipts } : null,
      // The fixes first, THEN the optional questions (Allen's rerun, 2026-10-03): Next asks them, tail last.
      primary: questionsPending ? { label: 'Next ›', action: 'next' } : { label: 'See it walk ›', action: 'see-walk' },
      secondary: null,
      stage: 'headshot',
    });
  }
  return screen({ id: 'fixing', rail: RAIL_FIX, title: 'Fixing…', body: [`Harry is fixing ${name} and checking it again.`], primary: null, secondary: null, stage: 'headshot', busy: true });
}

/** F and its failures (design §3.4 F, the failure table F1, F3, F4; F5 — a cancelled save — changes nothing). */
function sendScreen(send: IntakeSend, saved: boolean, name: string, worldTab: 'open' | 'gone', notice: string | null, shown: StageView): StepScreen {
  // The walk keeps playing behind Send when Check showed it; otherwise their face, at rest.
  switch (send.status) {
    case 'sending':
      return screen({ id: 'sending', rail: RAIL_SEND, title: 'Sending…', body: [`Harry is handing ${name} to World.`], primary: null, secondary: null, stage: shown, busy: true });
    case 'blocked':
      return screen({
        id: 'storage-blocked',
        rail: RAIL_SEND,
        title: `Harry couldn't hand ${name} to World`,
        body: ['Your browser blocked storage. Save the file, then upload it in World yourself.', ...(saved ? ['Saved.'] : [])],
        disclosure: { label: 'Why', lines: [send.reason] },
        primary: { label: 'Save the file', action: 'save-copy' },
        secondary: { label: 'Back to World', action: 'back-to-world' },
        stage: shown,
        notice,
      });
    case 'no-user':
      return screen({
        id: 'save-for-world',
        rail: RAIL_SEND,
        title: 'Save for World',
        body: [`${name} didn't come from World's upload, so World can't pick it up from Harry. Save it, then upload it in World.`, ...(saved ? ['Saved.'] : [])],
        primary: { label: 'Save for World upload', action: 'save-copy' },
        secondary: { label: 'Back to World', action: 'back-to-world' },
        stage: shown,
        notice,
      });
    case 'stage-changed':
      return screen({
        id: 'send-stage-changed',
        rail: RAIL_SEND,
        title: 'Another file was opened',
        body: [`Another file was opened after Harry fixed ${name}. Nothing was sent.`],
        primary: { label: 'Fix it again ›', action: 'fix-again' },
        secondary: { label: '‹ Back', action: 'back' },
        stage: 'headshot',
      });
    case 'sent':
    default:
      return screen({
        id: 'sent',
        rail: RAIL_DONE,
        title: 'Saved and sent to World',
        body:
          worldTab === 'gone'
            ? [`Open World and go to your avatar upload. The fixed ${name} will be waiting for a day.`, ...(saved ? ['A copy is saved too.'] : [])]
            : [`Switch back to your World tab. It will offer you the fixed ${name}.`, ...(saved ? ['A copy is saved too.'] : [])],
        primary: { label: 'Back to World ›', action: 'back-to-world' },
        // Allen, 2026-10-03: the editor stays reachable — the guided flow leads INTO it, never hides it.
        secondary: { label: 'Keep improving in Harry ›', action: 'keep-improving' },
        also: { label: 'Save a copy', action: 'save-copy' },
        stage: shown,
        notice,
      });
  }
}

function checkScreen(flow: IntakeFlow, name: string): StepScreen {
  if (flow.walk.status === 'unavailable') {
    return screen({
      id: 'check-no-walk',
      rail: RAIL_CHECK,
      title: `This is how ${name} walks in World`,
      body: ["Harry couldn't load World's walk to show you. The fix itself passed World's check."],
      disclosure: { label: 'Why', lines: [flow.walk.reason] },
      primary: { label: 'Save and send to World ›', action: 'send' },
      secondary: { label: 'Try again', action: 'retry-walk' },
      stage: 'headshot',
    });
  }
  return screen({
    id: 'check',
    rail: RAIL_CHECK,
    title: `This is how ${name} walks in World`,
    body: [flow.walk.status === 'playing' ? 'Arms and legs should swing without twisting.' : "Loading World's walk…"],
    // No Send while the walk is still loading (Codex P2, #243): Check is shown, or says it could not be.
    primary: flow.walk.status === 'loading' ? null : { label: 'Save and send to World ›', action: 'send' },
    secondary: { label: '‹ Back', action: 'back' },
    stage: 'proof',
    busy: flow.walk.status === 'loading',
  });
}
